/**
 * SettingsTab — Phase 6 (P1) Central Admin settings.
 *
 * Single-page tab with sidebar nav, 7 sections per the V1 spec:
 *   Alertes · Acheminement · Notifications · Messages & Bannières
 *   · Carte/Tableau · Sécurité & Sessions · Médias & Preuves.
 *
 * All writes go through PATCH /api/admin/settings (CSRF-protected,
 * audited via "settings.updated"). Sections that need a feature not
 * yet wired show a clear "applicable dans la version suivante" note.
 *
 * The component intentionally keeps every section as a small local
 * component so future iterations can extract individual sections
 * without touching the others.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  adminGetSettings, adminPatchSettings,
  adminForceLogoutAllAgencies, adminForceLogoutAllAdmins,
  adminListAgencies,
} from "../../lib/api";
import { settingsCache } from "../../lib/settingsCache";
import {
  Settings as SettingsIcon, Bell, BellOff, Loader2, Save, RotateCcw,
  Siren, Route, Megaphone, Map as MapIcon, Lock, FileText, Image as ImageIcon,
  AlertTriangle, ShieldAlert, Info,
} from "lucide-react";
import { EMERGENCY_FR } from "../../i18n.jsx";
import { EMERGENCY_TYPES } from "../EmergencyTypeGrid";
import { getCountryConfig } from "../../lib/countryConfig";

const _ccName = (getCountryConfig() || {}).name || "Mali";

// ── Section registry ─────────────────────────────────────────────────
const SECTIONS = [
  { key: "alerts",         label: "Alertes",            icon: Siren },
  { key: "routing",        label: "Acheminement",       icon: Route },
  { key: "notifications",  label: "Notifications",      icon: Bell },
  { key: "banners",        label: "Messages & Bannières", icon: Megaphone },
  { key: "map",            label: "Carte / Tableau",    icon: MapIcon },
  { key: "security",       label: "Sécurité & Sessions", icon: Lock },
  { key: "media",          label: "Médias & Preuves",   icon: ImageIcon },
];

// ── Reusable primitives ──────────────────────────────────────────────
const Field = ({ label, hint, children, testid }) => (
  <div className="flex flex-col gap-1" data-testid={testid}>
    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
      {label}
    </label>
    {children}
    {hint && <span className="text-[10px] text-zinc-500 font-mono">{hint}</span>}
  </div>
);

const Toggle = ({ value, onChange, testid, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!value}
    onClick={() => !disabled && onChange(!value)}
    data-testid={testid}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
      value ? "bg-emerald-500" : "bg-zinc-700"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        value ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const NumInput = ({ value, onChange, min, max, testid, step = 1, suffix }) => (
  <div className="flex items-center gap-2">
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-24 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
    />
    {suffix && <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">{suffix}</span>}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const TextArea = ({ value, onChange, testid, rows = 3, placeholder }) => (
  <textarea
    value={value || ""}
    rows={rows}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 placeholder-zinc-600 focus:border-emerald-500/50 focus:outline-none font-mono"
  />
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-2 border-b border-zinc-800 pb-2 mb-3">
    {Icon && <Icon className="w-4 h-4 text-emerald-400 mt-0.5" />}
    <div>
      <h3 className="text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-200">{title}</h3>
      {subtitle && <p className="text-[10px] text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const PendingBadge = () => (
  <span
    title="Cette préférence est enregistrée mais n'est pas encore appliquée aux notifications en direct."
    className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-300 bg-amber-500/5 rounded inline-flex items-center gap-1"
  >
    <Info className="w-2.5 h-2.5" /> V2
  </span>
);

const DisabledChannelNotice = ({ channel, meta }) => {
  if (!meta) return null;
  return (
    <div className="text-[10px] text-amber-300 font-mono mt-1" data-testid={`channel-${channel}-disabled-notice`}>
      Désactivé{meta.disabled_by_name ? ` par ${meta.disabled_by_name}` : ""}
      {meta.disabled_at ? ` · ${new Date(meta.disabled_at).toLocaleString("fr-FR")}` : ""}
    </div>
  );
};

// ── Section components ───────────────────────────────────────────────
const AlertsSection = ({ s, set }) => (
  <>
    <SectionHeader
      icon={Siren}
      title="Workflow des alertes"
      subtitle="Réglages utilisés par le tableau opérationnel"
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Fenêtre par défaut du tableau" testid="field-default-window">
        <Select
          value={s.default_dashboard_window}
          onChange={(v) => set("default_dashboard_window", v)}
          options={[
            { value: "24h", label: "Dernières 24h" },
            { value: "today", label: "Aujourd'hui" },
            { value: "7d", label: "7 jours" },
            { value: "30d", label: "30 jours" },
          ]}
          testid="select-default-window"
        />
      </Field>
      <Field label="Filtre par défaut" testid="field-default-filter">
        <Select
          value={s.default_alert_filter}
          onChange={(v) => set("default_alert_filter", v)}
          options={[
            { value: "active", label: "Actives / non-résolues" },
            { value: "all", label: "Toutes" },
          ]}
          testid="select-default-filter"
        />
      </Field>
      <Field label="Seuil d'attente (en attente trop long)" hint="Min. 5, max. 240" testid="field-pending-threshold">
        <NumInput
          value={s.pending_too_long_minutes}
          onChange={(v) => set("pending_too_long_minutes", v)}
          min={5} max={240}
          suffix="min"
          testid="input-pending-minutes"
        />
      </Field>
      <Field label="Seuil d'alerte non-assignée" testid="field-unassigned-threshold">
        <NumInput
          value={s.unassigned_warning_minutes}
          onChange={(v) => set("unassigned_warning_minutes", v)}
          min={5} max={240} suffix="min"
          testid="input-unassigned-minutes"
        />
      </Field>
      <Field label="Inclure types sensibles dans Critiques" testid="field-critical-sensitive">
        <Toggle
          value={s.critical_includes_sensitive}
          onChange={(v) => set("critical_includes_sensitive", v)}
          testid="toggle-critical-sensitive"
        />
      </Field>
      <Field label="Inclure non-assignées en retard dans Critiques" testid="field-critical-overdue">
        <Toggle
          value={s.critical_includes_overdue_unassigned}
          onChange={(v) => set("critical_includes_overdue_unassigned", v)}
          testid="toggle-critical-overdue"
        />
      </Field>
    </div>
  </>
);

const RoutingSection = ({ s, set, agencies }) => {
  const routing = s.routing_suggestions || {};
  const highRisk = s.high_risk_categories || [];
  return (
    <>
      <SectionHeader
        icon={Route}
        title="Acheminement suggéré"
        subtitle="Suggestions affichées au Central Admin. Le dispatch reste manuel."
      />
      <div className="space-y-3" data-testid="routing-suggestions">
        {EMERGENCY_TYPES.map(({ key }) => {
          const label = EMERGENCY_FR[key]?.label || key;
          const selected = new Set(routing[key] || []);
          const isHighRisk = highRisk.includes(key);
          return (
            <div key={key} className="border border-zinc-800 bg-[#0b0f14] rounded p-3" data-testid={`routing-row-${key}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-xs font-bold text-zinc-200">{label}</span>
                <button
                  type="button"
                  onClick={() => set(
                    "high_risk_categories",
                    isHighRisk ? highRisk.filter((x) => x !== key) : [...highRisk, key],
                  )}
                  data-testid={`high-risk-toggle-${key}`}
                  className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                    isHighRisk
                      ? "border-rose-500/60 text-rose-300 bg-rose-500/10"
                      : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {isHighRisk ? "À haut risque" : "Risque normal"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {agencies.length === 0 ? (
                  <span className="text-[10px] text-zinc-500 italic">Aucune Agence enregistrée — créez-en dans l&apos;onglet Agences.</span>
                ) : agencies.map((ag) => {
                  const checked = selected.has(ag.id);
                  return (
                    <button
                      key={ag.id}
                      type="button"
                      onClick={() => {
                        const next = { ...routing };
                        const cur = new Set(next[key] || []);
                        if (checked) cur.delete(ag.id); else cur.add(ag.id);
                        next[key] = Array.from(cur);
                        if (next[key].length === 0) delete next[key];
                        set("routing_suggestions", next);
                      }}
                      data-testid={`routing-agency-${key}-${ag.id}`}
                      className={`text-[10px] px-2 py-0.5 rounded border ${
                        checked
                          ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                          : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      {ag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500 mt-3 font-mono">
        Les suggestions s&apos;affichent dans le panneau de dispatch — l&apos;Admin garde le contrôle de l&apos;envoi final.
      </p>
    </>
  );
};

const NotifRow = ({ s, set, label, k, channel, killSwitchMeta }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-zinc-800 last:border-b-0" data-testid={`notif-row-${channel}`}>
    <div>
      <div className="text-xs font-bold text-zinc-200">{label}</div>
      <DisabledChannelNotice channel={channel} meta={!s[k] ? killSwitchMeta[channel] : null} />
    </div>
    <Toggle value={s[k]} onChange={(v) => set(k, v)} testid={`toggle-${channel}`} />
  </div>
);

const NotificationsSection = ({ s, set }) => {
  const meta = s.kill_switch_meta || {};
  return (
    <>
      <SectionHeader
        icon={Bell}
        title="Contrôles de notification"
        subtitle="Désactiver un canal n&apos;empêche jamais une alerte d&apos;être créée — seul l&apos;envoi est ignoré et tracé dans l&apos;audit."
      />
      <NotifRow s={s} set={set} label="SMS (Africa's Talking / Twilio)" k="sms_enabled" channel="sms" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Email (Resend)" k="email_enabled" channel="email" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Push admin (Web Push)" k="push_admin_enabled" channel="push_admin" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Push citoyens" k="push_citizen_enabled" channel="push_citizen" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Diffusions (broadcasts)" k="broadcast_enabled" channel="broadcast" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Notifications Agence" k="agency_notifications_enabled" channel="agency" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="Son sur le tableau Admin" k="admin_sound_enabled" channel="admin_sound" killSwitchMeta={meta} />
      <NotifRow s={s} set={set} label="WebSocket temps réel (Admin)" k="admin_ws_enabled" channel="admin_ws" killSwitchMeta={meta} />
      <div className="mt-4">
        <SectionHeader
          icon={FileText}
          title="Modèles de notifications"
          subtitle="Applicable dans la version suivante — édition possible dès maintenant."
        />
        <div className="space-y-3">
          {Object.entries(s.templates || {}).map(([key, value]) => (
            <Field
              key={key}
              label={
                <span className="flex items-center gap-2">
                  {key.replace(/_/g, " ")} <PendingBadge />
                </span>
              }
              testid={`field-template-${key}`}
            >
              <TextArea
                value={value}
                onChange={(v) => {
                  const next = { ...(s.templates || {}) };
                  next[key] = v;
                  set("templates", next);
                }}
                rows={2}
                testid={`template-${key}`}
              />
            </Field>
          ))}
        </div>
      </div>
    </>
  );
};

const BannersSection = ({ s, set }) => {
  const banner = s.admin_banner || { text: "", active: false, tone: "info" };
  const maint = s.maintenance_notice || { text: "", active: false };
  const coord = s.emergency_coordination_note || { text: "", active: false };
  return (
    <>
      <SectionHeader
        icon={Megaphone}
        title="Messages & bannières"
        subtitle="Visibles dans l'interface Admin. Les diffusions citoyennes restent gérées dans l'onglet Diffusion."
      />
      <div className="space-y-4">
        <div className="border border-zinc-800 bg-[#0b0f14] rounded p-3" data-testid="banner-admin">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-zinc-200">Bannière Admin</span>
            <Toggle
              value={banner.active}
              onChange={(v) => set("admin_banner", { ...banner, active: v })}
              testid="toggle-admin-banner"
            />
          </div>
          <TextArea
            value={banner.text}
            onChange={(v) => set("admin_banner", { ...banner, text: v })}
            rows={2}
            placeholder="Ex: Période d'activité accrue — vigilance renforcée."
            testid="admin-banner-text"
          />
          <div className="mt-2">
            <Select
              value={banner.tone || "info"}
              onChange={(v) => set("admin_banner", { ...banner, tone: v })}
              options={[
                { value: "info", label: "Info" },
                { value: "warn", label: "Avertissement" },
                { value: "danger", label: "Critique" },
              ]}
              testid="admin-banner-tone"
            />
          </div>
        </div>
        <div className="border border-zinc-800 bg-[#0b0f14] rounded p-3" data-testid="banner-maintenance">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-zinc-200 flex items-center gap-2">
              Service dégradé / maintenance <PendingBadge />
            </span>
            <Toggle
              value={maint.active}
              onChange={(v) => set("maintenance_notice", { ...maint, active: v })}
              testid="toggle-maintenance"
            />
          </div>
          <TextArea
            value={maint.text}
            onChange={(v) => set("maintenance_notice", { ...maint, text: v })}
            rows={2}
            placeholder="Service dégradé — les SMS peuvent être retardés."
            testid="maintenance-text"
          />
          <p className="text-[10px] text-zinc-500 mt-1 font-mono">
            N&apos;empêche jamais la création d&apos;alertes — message d&apos;information uniquement.
          </p>
        </div>
        <div className="border border-zinc-800 bg-[#0b0f14] rounded p-3" data-testid="banner-coordination">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-zinc-200 flex items-center gap-2">
              Instruction de coordination <PendingBadge />
            </span>
            <Toggle
              value={coord.active}
              onChange={(v) => set("emergency_coordination_note", { ...coord, active: v })}
              testid="toggle-coordination"
            />
          </div>
          <TextArea
            value={coord.text}
            onChange={(v) => set("emergency_coordination_note", { ...coord, text: v })}
            rows={2}
            placeholder="Coordination spéciale en cours — protocole X activé."
            testid="coordination-text"
          />
        </div>
      </div>
    </>
  );
};

const MapSection = ({ s, set }) => (
  <>
    <SectionHeader
      icon={MapIcon}
      title="Comportement par défaut du tableau"
      subtitle={`Carte centrée ${_ccName}. Pas de carte mondiale, pas de sélecteur pays.`}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Mode carte par défaut" testid="field-map-mode">
        <Select
          value={s.default_map_mode}
          onChange={(v) => set("default_map_mode", v)}
          options={[
            { value: "markers", label: "Marqueurs" },
            { value: "heatmap", label: "Heatmap" },
          ]}
          testid="select-map-mode"
        />
      </Field>
      <Field label="Fenêtre par défaut (synchronisé avec Alertes)" testid="field-map-window">
        <Select
          value={s.default_dashboard_window}
          onChange={(v) => set("default_dashboard_window", v)}
          options={[
            { value: "24h", label: "24h" },
            { value: "today", label: "Aujourd'hui" },
            { value: "7d", label: "7j" },
            { value: "30d", label: "30j" },
          ]}
          testid="select-map-window"
        />
      </Field>
      <Field label="Afficher les alertes résolues sur la carte" testid="field-show-resolved">
        <Toggle
          value={s.show_resolved_on_map}
          onChange={(v) => set("show_resolved_on_map", v)}
          testid="toggle-show-resolved"
        />
      </Field>
      <Field label="Afficher les anciennes alertes par défaut" testid="field-show-old">
        <Toggle
          value={s.show_old_alerts_on_map}
          onChange={(v) => set("show_old_alerts_on_map", v)}
          testid="toggle-show-old"
        />
      </Field>
    </div>
    <p className="text-[10px] text-zinc-500 mt-3 font-mono">
      Tableau toujours centré sur {_ccName === "Mali" ? "le Mali" : "la " + _ccName} — vue nationale par défaut.
    </p>
  </>
);

const SecuritySection = ({ s, set, onForceLogoutAllAgencies, onForceLogoutAllAdmins }) => {
  const [confirming, setConfirming] = useState(null); // 'agencies' | 'admins' | null
  return (
    <>
      <SectionHeader
        icon={Lock}
        title="Sécurité & sessions"
        subtitle="Contrôles globaux. Les actions par-Agence restent dans l'onglet Agences."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Délai de session (minutes)" hint="15 à 43 200 (30 jours)" testid="field-session-timeout">
          <NumInput
            value={s.session_timeout_minutes}
            onChange={(v) => set("session_timeout_minutes", v)}
            min={15} max={43200} suffix="min"
            testid="input-session-timeout"
          />
        </Field>
        <Field label="Tentatives avant verrouillage" testid="field-lockout">
          <NumInput
            value={s.failed_login_lockout_attempts}
            onChange={(v) => set("failed_login_lockout_attempts", v)}
            min={3} max={20}
            testid="input-lockout"
          />
        </Field>
        <Field label="Mot de passe fort obligatoire" testid="field-strong-pw">
          <div className="flex items-center gap-2">
            <Toggle
              value={s.strong_password_required}
              onChange={(v) => set("strong_password_required", v)}
              testid="toggle-strong-pw"
            />
            <PendingBadge />
          </div>
        </Field>
      </div>
      <div className="mt-4 border border-rose-500/30 bg-rose-500/5 rounded p-3 space-y-2" data-testid="danger-zone">
        <div className="text-[11px] font-bold uppercase tracking-widest text-rose-300 flex items-center gap-2">
          <ShieldAlert className="w-3.5 h-3.5" /> Actions critiques
        </div>
        <div className="text-[10px] text-zinc-400 font-mono">
          Les déconnexions forcées sont auditées. Les Agences/Admins devront se reconnecter.
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {confirming === "agencies" ? (
            <>
              <span className="text-[10px] text-rose-300 font-mono self-center">Confirmer ?</span>
              <button
                type="button"
                onClick={async () => { await onForceLogoutAllAgencies(); setConfirming(null); }}
                data-testid="confirm-logout-all-agencies"
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-rose-500/60 text-rose-200 bg-rose-500/15 hover:bg-rose-500/25 rounded"
              >
                Oui, déconnecter tout
              </button>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-400 rounded"
              >
                Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming("agencies")}
              data-testid="btn-logout-all-agencies"
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 rounded"
            >
              Déconnecter toutes les Agences
            </button>
          )}
          {confirming === "admins" ? (
            <>
              <span className="text-[10px] text-rose-300 font-mono self-center">Confirmer (vous serez déconnecté) ?</span>
              <button
                type="button"
                onClick={async () => { await onForceLogoutAllAdmins(); setConfirming(null); }}
                data-testid="confirm-logout-all-admins"
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-rose-500/60 text-rose-200 bg-rose-500/15 hover:bg-rose-500/25 rounded"
              >
                Oui, tout invalider
              </button>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-400 rounded"
              >
                Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming("admins")}
              data-testid="btn-logout-all-admins"
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 rounded"
            >
              Déconnecter toutes les sessions Admin
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const MediaSection = ({ s, set }) => (
  <>
    <SectionHeader
      icon={ImageIcon}
      title="Médias & preuves"
      subtitle="Règles de manipulation des pièces jointes par les Agences assignées."
    />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Field label="Taille max. photo" testid="field-max-image">
        <NumInput
          value={s.max_image_upload_mb}
          onChange={(v) => set("max_image_upload_mb", v)}
          min={1} max={50} suffix="MB"
          testid="input-max-image"
        />
      </Field>
      <Field label="Taille max. vidéo" testid="field-max-video">
        <NumInput
          value={s.max_video_upload_mb}
          onChange={(v) => set("max_video_upload_mb", v)}
          min={1} max={500} suffix="MB"
          testid="input-max-video"
        />
      </Field>
      <Field label="Taille max. audio" testid="field-max-audio">
        <NumInput
          value={s.max_audio_upload_mb}
          onChange={(v) => set("max_audio_upload_mb", v)}
          min={1} max={200} suffix="MB"
          testid="input-max-audio"
        />
      </Field>
      <Field label="Agences peuvent télécharger les preuves" hint="Par défaut: visualisation uniquement" testid="field-can-download">
        <Toggle
          value={s.agency_can_download_evidence}
          onChange={(v) => set("agency_can_download_evidence", v)}
          testid="toggle-can-download"
        />
      </Field>
      <Field label="Avertissement preuves sensibles" testid="field-sensitive-warn">
        <Toggle
          value={s.sensitive_evidence_warning_enabled}
          onChange={(v) => set("sensitive_evidence_warning_enabled", v)}
          testid="toggle-sensitive-warn"
        />
      </Field>
    </div>
    <p className="text-[10px] text-zinc-500 mt-3 font-mono">
      Les Agences ne peuvent pas supprimer une preuve. Tout accès est tracé dans l&apos;Audit.
    </p>
  </>
);

// ── Container ────────────────────────────────────────────────────────
export const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({}); // changed keys → new value
  const [activeSection, setActiveSection] = useState("alerts");
  const [agencies, setAgencies] = useState([]);
  const lastSavedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminGetSettings(),
      adminListAgencies().catch(() => []),
    ]).then(([res, ags]) => {
      if (cancelled) return;
      setSettings(res.settings);
      setDefaults(res.meta?.defaults || {});
      setAgencies(Array.isArray(ags) ? ags : []);
      lastSavedRef.current = res.settings;
      settingsCache.set(res.settings || {});
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Impossible de charger les paramètres");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Merged view: persisted settings overlaid with unsaved edits.
  const view = useMemo(() => ({ ...(settings || {}), ...dirty }), [settings, dirty]);

  const set = (key, value) => {
    setDirty((prev) => ({ ...prev, [key]: value }));
  };

  const dirtyCount = Object.keys(dirty).length;

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const res = await adminPatchSettings(dirty);
      setSettings(res.settings);
      lastSavedRef.current = res.settings;
      settingsCache.set(res.settings || {});
      setDirty({});
      toast.success(`Paramètres enregistrés (${dirtyCount} modification${dirtyCount > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setDirty({});
    toast.info("Modifications annulées");
  };

  const onForceLogoutAllAgencies = async () => {
    try {
      const r = await adminForceLogoutAllAgencies();
      toast.success(`${r.sessions_invalidated} session(s) Agence révoquée(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };
  const onForceLogoutAllAdmins = async () => {
    try {
      const r = await adminForceLogoutAllAdmins();
      toast.success(`${r.sessions_invalidated} session(s) Admin révoquée(s) — reconnexion requise`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex-1 flex items-center justify-center py-12 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const sectionContent = (() => {
    switch (activeSection) {
      case "alerts":        return <AlertsSection s={view} set={set} />;
      case "routing":       return <RoutingSection s={view} set={set} agencies={agencies} />;
      case "notifications": return <NotificationsSection s={view} set={set} />;
      case "banners":       return <BannersSection s={view} set={set} />;
      case "map":           return <MapSection s={view} set={set} />;
      case "security":      return (
        <SecuritySection
          s={view} set={set}
          onForceLogoutAllAgencies={onForceLogoutAllAgencies}
          onForceLogoutAllAdmins={onForceLogoutAllAdmins}
        />
      );
      case "media":         return <MediaSection s={view} set={set} />;
      default:              return null;
    }
  })();
  void defaults; // reserved for a future per-field "reset to default" affordance

  return (
    <div className="px-3 sm:px-5 py-4 pb-8" data-testid="settings-tab">
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2 mb-4">
        <SettingsIcon className="w-4 h-4 text-emerald-400" />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-200">
          Paramètres
        </span>
        {dirtyCount > 0 && (
          <span
            data-testid="settings-dirty-badge"
            className="ml-2 text-[9px] font-bold uppercase tracking-widest border border-amber-500/40 text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded"
          >
            {dirtyCount} modif. non enregistrée{dirtyCount > 1 ? "s" : ""}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {dirtyCount > 0 && (
            <button
              type="button"
              onClick={discard}
              data-testid="settings-discard"
              className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-zinc-100 rounded inline-flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Annuler
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={dirtyCount === 0 || saving}
            data-testid="settings-save"
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded inline-flex items-center gap-1.5 ${
              dirtyCount === 0
                ? "border border-zinc-800 text-zinc-600"
                : "border border-emerald-500/60 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/25"
            }`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Enregistrer
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible cmd-scroll-row" data-testid="settings-nav">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const active = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSection(key)}
                data-testid={`settings-nav-${key}`}
                className={`shrink-0 md:shrink text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border rounded inline-flex items-center gap-2 ${
                  active
                    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                    : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </nav>
        <div className="border border-zinc-800 bg-[#0b0f14] rounded p-4 min-h-[400px]" data-testid="settings-content">
          {sectionContent}
        </div>
      </div>
      <div className="mt-3 text-[10px] text-zinc-600 font-mono">
        Dernière mise à jour: {settings.updated_at ? new Date(settings.updated_at).toLocaleString("fr-FR") : "—"}
      </div>
    </div>
  );
};

export default SettingsTab;
